import { handleError } from 'utils/error'
import axios from '../base'
import {
  CancelContractCourtRequest,
  ContractCourtMutationResponse,
  CreateContractCourtRequest,
  GetContractCourtByIdRequest,
  GetContractCourtsRequest,
  GetContractCourtsResponse,
  PreviewCancellationRequest,
  PreviewResponse,
  PreviewShorteningRequest,
  UpdateEndDateRequest,
} from './contract-court.types'

export class ContractCourtService {
  private locationsUrl = '/locations'
  private contractsUrl = '/contract_courts'

  listContractCourts = async (
    fields: GetContractCourtsRequest
  ): Promise<GetContractCourtsResponse> => {
    const { locationId, payment_status, discarded, page, limit } = fields
    const params: string[] = []
    if (payment_status) params.push(`payment_status=${payment_status}`)
    if (discarded) params.push('discarded=true')
    if (page != null) params.push(`page=${page}`)
    if (limit != null) params.push(`limit=${limit}`)
    const query = params.length ? `?${params.join('&')}` : ''
    const url = `${this.locationsUrl}/${locationId}/contract_courts${query}`
    const { data } = await axios.get<GetContractCourtsResponse>(url)
    if (data.error) {
      handleError(Error('Error en listContractCourts'))
      return {} as GetContractCourtsResponse
    }
    return data
  }

  getContractCourt = async (
    fields: GetContractCourtByIdRequest
  ): Promise<ContractCourtMutationResponse> => {
    const { contractId } = fields
    const { data } = await axios.get<ContractCourtMutationResponse>(
      `${this.contractsUrl}/${contractId}`
    )
    if (data.error) {
      handleError(Error('Error en getContractCourt'))
      return {} as ContractCourtMutationResponse
    }
    return data
  }

  createContractCourt = async (
    fields: CreateContractCourtRequest
  ): Promise<ContractCourtMutationResponse> => {
    const { locationId, body } = fields
    const { data } = await axios.post<ContractCourtMutationResponse>(
      `${this.locationsUrl}/${locationId}/contract_courts`,
      body
    )
    if (data.error) {
      handleError(Error('Error en createContractCourt'))
    }
    return data
  }

  cancelContractCourt = async (
    fields: CancelContractCourtRequest
  ): Promise<ContractCourtMutationResponse> => {
    const { contractId, resolution } = fields
    // axios.delete supports a body via the `data` config field — Phase 6
    // backend reads the resolution choice from request body.
    const { data } = await axios.delete<ContractCourtMutationResponse>(
      `${this.contractsUrl}/${contractId}`,
      { data: { resolution } }
    )
    if (data.error) {
      handleError(Error('Error en cancelContractCourt'))
    }
    return data
  }

  previewCancellation = async (
    fields: PreviewCancellationRequest
  ): Promise<PreviewResponse> => {
    const { contractId } = fields
    const { data } = await axios.post<PreviewResponse>(
      `${this.contractsUrl}/${contractId}/preview_cancellation`
    )
    if (data.error) {
      handleError(Error('Error en previewCancellation'))
    }
    return data
  }

  previewShortening = async (
    fields: PreviewShorteningRequest
  ): Promise<PreviewResponse> => {
    const { contractId, new_end_date } = fields
    const { data } = await axios.post<PreviewResponse>(
      `${this.contractsUrl}/${contractId}/preview_shortening`,
      { new_end_date }
    )
    if (data.error) {
      handleError(Error('Error en previewShortening'))
    }
    return data
  }

  updateEndDate = async (
    fields: UpdateEndDateRequest
  ): Promise<ContractCourtMutationResponse> => {
    const { contractId, new_end_date, resolution, court_ids } = fields
    const { data } = await axios.patch<ContractCourtMutationResponse>(
      `${this.contractsUrl}/${contractId}/end_date`,
      { new_end_date, resolution, court_ids }
    )
    if (data.error) {
      handleError(Error('Error en updateEndDate'))
    }
    return data
  }
}

export default new ContractCourtService()
